import { Bot, Globe, Settings, Code, Workflow, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import ConsultationModal from './ConsultationModal';
import EditableText from './cms/EditableText';

const ServicesSection = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);

  const services = [
    {
      icon: Bot,
      titleKey: 'services.aiTitle',
      descriptionKey: 'services.aiDescription',
      featuresKeys: ['features.customAI', 'features.processAutomation', 'features.chatbots', 'features.analytics'],
      gradient: "from-primary to-primary-glow",
      route: "/services/ai-solutions"
    },
    {
      icon: Globe,
      titleKey: 'services.webTitle',
      descriptionKey: 'services.webDescription',
      featuresKeys: ['features.webDesign', 'features.seoOptimization', 'features.ecommerce', 'features.performance'],
      gradient: "from-accent to-secondary",
      route: "/services/web-seo"
    },
    {
      icon: Settings,
      titleKey: 'services.itTitle',
      descriptionKey: 'services.itDescription',
      featuresKeys: ['features.techStrategy', 'features.infrastructure', 'features.security', 'features.aiConsulting', 'features.digitalTransformation'],
      gradient: "from-secondary to-accent",
      route: "/services/it-consulting"
    },
    {
      icon: Code,
      titleKey: 'services.softwareTitle',
      descriptionKey: 'services.softwareDescription',
      featuresKeys: ['features.customApps', 'features.mobileDev', 'features.enterpriseSystems', 'features.apiIntegration'],
      gradient: "from-primary-glow to-primary",
      route: "/services/software-development"
    },
    {
      icon: Workflow,
      titleKey: 'services.socialTitle',
      descriptionKey: 'services.socialDescription',
      featuresKeys: ['features.socialStrategy', 'features.contentCreation', 'features.adManagement', 'features.reporting'],
      gradient: "from-accent to-primary",
      route: "/services/automate-workflows"
    }
  ];

  return (
    <section id="services" className="py-24 px-4 relative">
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Sparkles className="w-4 h-4 text-primary" />
            <EditableText k="services.badge" className="text-sm font-medium text-primary" placeholder="Our Services" />
          </div>
          <h2 className="text-4xl md:text-7xl font-display font-bold mb-8">
            <EditableText k="services.title" /> <EditableText k="services.titleHighlight" className="gradient-text" />
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            <EditableText k="services.subtitle" />
          </p>
        </div>

        {/* Services Grid - Tablet: 2-2-1 layout, Desktop: 3-2 layout */}
        {/* Mobile & Tablet: Single grid with responsive columns */}
        <div className="lg:hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-8">
            {services.map((service, index) => (
              <div 
                key={service.titleKey} 
                className={`group relative transform transition-all duration-300 hover:scale-[1.03] ${
                  index === 4 ? 'md:col-span-2 md:max-w-lg md:mx-auto' : ''
                }`}
              >
                {/* Glassmorphism Card */}
                <div className="relative h-full p-6 sm:p-8 rounded-3xl card-gradient glass-card border border-border/50 flex flex-col min-h-[480px] backdrop-blur-md shadow-xl hover:shadow-2xl hover:border-primary/30 transition-all duration-300 overflow-hidden">
                  <div aria-hidden="true" className="glass-shine"></div>
                  <div aria-hidden="true" className="glass-bottom-glow"></div>

                  {/* Content */}
                  <h3 className="text-2xl font-display font-bold mb-4 text-foreground">
                    <EditableText k={service.titleKey} />
                  </h3>
                  
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    <EditableText k={service.descriptionKey} />
                  </p>

                  {/* Features */}
                  <ul className="space-y-3 mb-8 flex-grow">
                    {service.featuresKeys.map((featureKey) => (
                      <li key={featureKey} className="flex items-center text-sm text-foreground">
                        <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                        <EditableText k={featureKey} />
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button 
                    variant="outline" 
                    className="w-full border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground mt-auto"
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      navigate(service.route);
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <EditableText k="services.learnMore" />
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop: First 3 cards */}
        <div className="hidden lg:block">
          <div className="grid grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
            {services.slice(0, 3).map((service) => (
              <div key={service.titleKey} className="group relative transform transition-all duration-300 hover:scale-[1.03]">
                {/* Glassmorphism Card */}
                <div className="relative h-full p-6 sm:p-8 rounded-3xl card-gradient glass-card border border-border/50 flex flex-col min-h-[480px] backdrop-blur-md shadow-xl hover:shadow-2xl hover:border-primary/30 transition-all duration-300 overflow-hidden">
                  <div aria-hidden="true" className="glass-shine"></div>
                  <div aria-hidden="true" className="glass-bottom-glow"></div>

                  {/* Content */}
                  <h3 className="text-2xl font-display font-bold mb-4 text-foreground">
                    <EditableText k={service.titleKey} />
                  </h3>
                  
                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    <EditableText k={service.descriptionKey} />
                  </p>

                  {/* Features */}
                  <ul className="space-y-3 mb-8 flex-grow">
                    {service.featuresKeys.map((featureKey) => (
                      <li key={featureKey} className="flex items-center text-sm text-foreground">
                        <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                        <EditableText k={featureKey} />
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button 
                    variant="outline" 
                    className="w-full border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground mt-auto"
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      navigate(service.route);
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <EditableText k="services.learnMore" />
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Bottom 2 cards centered */}
          <div className="flex justify-center">
            <div className="grid grid-cols-2 gap-8 max-w-4xl">
              {services.slice(3, 5).map((service) => (
                <div key={service.titleKey} className="group relative transform transition-all duration-300 hover:scale-[1.03]">
                  {/* Glassmorphism Card */}
                  <div className="relative h-full p-6 sm:p-8 rounded-3xl card-gradient glass-card border border-border/50 flex flex-col min-h-[480px] backdrop-blur-md shadow-xl hover:shadow-2xl hover:border-primary/30 transition-all duration-300 overflow-hidden">
                    <div aria-hidden="true" className="glass-shine"></div>
                    <div aria-hidden="true" className="glass-bottom-glow"></div>

                    {/* Content */}
                    <h3 className="text-2xl font-display font-bold mb-4 text-foreground">
                      <EditableText k={service.titleKey} />
                    </h3>
                    
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                      <EditableText k={service.descriptionKey} />
                    </p>

                    {/* Features */}
                    <ul className="space-y-3 mb-8 flex-grow">
                      {service.featuresKeys.map((featureKey) => (
                        <li key={featureKey} className="flex items-center text-sm text-foreground">
                          <div className="w-2 h-2 bg-primary rounded-full mr-3"></div>
                          <EditableText k={featureKey} />
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <Button 
                      variant="outline" 
                      className="w-full border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground mt-auto"
                      onClick={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                        navigate(service.route);
                      }}
                    >
                      <span className="flex items-center gap-2">
                        <EditableText k="services.learnMore" />
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-20">
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-display font-bold mb-6">
              <EditableText k="services.ctaTitle" /> <EditableText k="services.ctaTitleHighlight" className="gradient-text" />
            </h3>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              <EditableText k="services.ctaText" />
            </p>
            <Button 
              size="lg" 
              className="gradient-primary text-sm sm:text-lg px-6 sm:px-10 py-6 rounded-full mx-auto"
              onClick={() => setIsConsultationOpen(true)}
            >
              <span className="flex items-center gap-2 text-center">
                <EditableText k="services.ctaButton" />
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
              </span>
            </Button>
          </div>
        </div>
      </div>

      {/* Consultation Modal */}
      <ConsultationModal 
        open={isConsultationOpen} 
        onOpenChange={setIsConsultationOpen} 
      />
    </section>
  );
};

export default ServicesSection;